import type {
  Track,
  Source,
  WaypointAttributes,
  DataType,
} from '../types/scenario';
import { haversineDistance } from '../generation/interpolation';

const METERS_PER_NAUTICAL_MILE = 1852;
const MS_PER_HOUR = 3_600_000;
/** Cap calculated SOG to filter GPS noise spikes (knots). */
const MAX_REASONABLE_SOG_KN = 60;

/** Result of importing a GeoJSON file into the scenario model. */
export interface ImportResult {
  /** Scenario name — derived from filename or the embedded scenario_name. */
  name: string;
  /** ISO 8601 start time derived from earliest timestamp in the data. */
  start_time: string;
  /** ISO 8601 end time derived from latest timestamp in the data. */
  end_time: string;
  /** Parsed tracks ready to insert into a Scenario. */
  tracks: Track[];
  /** Bounding extent [minLon, minLat, maxLon, maxLat] for zoom-to-fit. */
  bounds: [number, number, number, number];
}

interface ParsedPoint {
  lon: number;
  lat: number;
  timestamp: string;
  timestampMs: number;
  properties: Record<string, unknown>;
}

/**
 * Import a GeoJSON Point FeatureCollection and convert it into Data Forge
 * Track objects. Points are grouped by vessel identity, sorted chronologically,
 * and converted to LineString paths with calculated waypoint attributes.
 *
 * Supported grouping keys (in priority order):
 *  1. `track_uuid` — re-importing our own export format
 *  2. `mmsi` — standard AIS identifier
 *  3. All points → single track (when no grouping key present)
 */
export function importGeoJsonTracks(
  geojson: unknown,
  options?: { filename?: string; colors?: string[] },
): ImportResult {
  const fc = validateFeatureCollection(geojson);
  const points = extractPoints(fc);

  if (points.length === 0) {
    throw new Error(
      'No valid Point features found in the GeoJSON file. Each point needs a ' +
        'timestamp and valid coordinates (lon -180..180, lat -90..90).',
    );
  }

  const groups = groupByVessel(points);
  const colors = options?.colors ?? DEFAULT_COLORS;

  const tracks: Track[] = [];
  let colorIdx = 0;

  for (const [_key, groupPoints] of groups) {
    const sorted = groupPoints.sort((a, b) => a.timestampMs - b.timestampMs);
    const deduped = deduplicateConsecutive(sorted);

    if (deduped.length < 2) continue;

    const track = buildTrack(deduped, colors[colorIdx % colors.length]);
    tracks.push(track);
    colorIdx++;
  }

  if (tracks.length === 0) {
    throw new Error(
      'Could not create any tracks. Each vessel needs at least 2 unique positions.',
    );
  }

  const allTimestamps = points.map((p) => p.timestampMs);
  const minTs = Math.min(...allTimestamps);
  const maxTs = Math.max(...allTimestamps);

  // Detect our own export format for round-trip support
  const fcProps = (fc as unknown as Record<string, unknown>).properties as
    | Record<string, unknown>
    | undefined;
  const scenarioName =
    typeof fcProps?.scenario_name === 'string'
      ? fcProps.scenario_name
      : options?.filename
        ? stripExtension(options.filename)
        : 'Imported Scenario';

  const startTime =
    typeof fcProps?.start_time === 'string'
      ? fcProps.start_time
      : new Date(minTs).toISOString();
  const endTime =
    typeof fcProps?.end_time === 'string'
      ? fcProps.end_time
      : new Date(maxTs).toISOString();

  const bounds = computeBounds(points);

  return {
    name: scenarioName,
    start_time: startTime,
    end_time: endTime,
    tracks,
    bounds,
  };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function validateFeatureCollection(input: unknown): GeoJSON.FeatureCollection {
  if (!input || typeof input !== 'object') {
    throw new Error('Invalid GeoJSON: input is not an object.');
  }
  const obj = input as Record<string, unknown>;
  if (obj.type !== 'FeatureCollection') {
    throw new Error(
      `Invalid GeoJSON: expected "FeatureCollection" but got "${String(obj.type)}".`,
    );
  }
  if (!Array.isArray(obj.features)) {
    throw new Error('Invalid GeoJSON: "features" is not an array.');
  }
  return input as GeoJSON.FeatureCollection;
}

// ---------------------------------------------------------------------------
// Point extraction
// ---------------------------------------------------------------------------

function extractPoints(fc: GeoJSON.FeatureCollection): ParsedPoint[] {
  const points: ParsedPoint[] = [];

  for (const feature of fc.features) {
    if (!feature || feature.type !== 'Feature') continue;
    if (!feature.geometry || feature.geometry.type !== 'Point') continue;

    const coords = feature.geometry.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) continue;

    const lon = coords[0];
    const lat = coords[1];
    // Reject non-numeric or AIS "not available" / out-of-range coordinates.
    // Transponder-off (signal gap) positions export the AIS sentinels
    // lon=181 / lat=91; importing them would create bogus jumps to null-island
    // (or, when a whole track is dark, leave no real positions at all).
    if (!isValidLonLat(lon, lat)) continue;

    const props = (feature.properties ?? {}) as Record<string, unknown>;
    const timestamp = (props.timestamp ?? props.time) as string | undefined;
    if (!timestamp) continue;

    const ms = new Date(timestamp).getTime();
    if (Number.isNaN(ms)) continue;

    points.push({
      lon,
      lat,
      timestamp,
      timestampMs: ms,
      properties: props,
    });
  }

  return points;
}

/** True when lon/lat are finite numbers within valid WGS84 ranges. */
function isValidLonLat(lon: unknown, lat: unknown): boolean {
  return (
    typeof lon === 'number' &&
    typeof lat === 'number' &&
    Number.isFinite(lon) &&
    Number.isFinite(lat) &&
    lon >= -180 &&
    lon <= 180 &&
    lat >= -90 &&
    lat <= 90
  );
}

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

function groupByVessel(points: ParsedPoint[]): Map<string, ParsedPoint[]> {
  const groups = new Map<string, ParsedPoint[]>();

  // Detect grouping key from the first point
  const sample = points[0].properties;
  const groupKey =
    typeof sample.track_uuid === 'string'
      ? 'track_uuid'
      : sample.mmsi != null
        ? 'mmsi'
        : null;

  for (const point of points) {
    const key =
      groupKey != null
        ? String(point.properties[groupKey] ?? '_default')
        : '_default';
    let group = groups.get(key);
    if (!group) {
      group = [];
      groups.set(key, group);
    }
    group.push(point);
  }

  return groups;
}

// ---------------------------------------------------------------------------
// Deduplication
// ---------------------------------------------------------------------------

/** Remove consecutive points with the same coordinates. */
function deduplicateConsecutive(sorted: ParsedPoint[]): ParsedPoint[] {
  if (sorted.length === 0) return sorted;
  const result = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const prev = result[result.length - 1];
    if (sorted[i].lon !== prev.lon || sorted[i].lat !== prev.lat) {
      result.push(sorted[i]);
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Track building
// ---------------------------------------------------------------------------

function buildTrack(points: ParsedPoint[], fallbackColor: string): Track {
  const coordinates = points.map((p) => [p.lon, p.lat]);
  const waypoints = buildWaypoints(points);
  const source = extractSource(points);
  const fractions = computeTimeFractions(points);

  // Prefer the embedded color for round-trip fidelity
  const color =
    typeof points[0].properties.color === 'string'
      ? points[0].properties.color
      : fallbackColor;

  return {
    uuid: crypto.randomUUID(),
    data_type: resolveDataType(points[0].properties),
    source,
    path: { type: 'LineString', coordinates },
    waypoints,
    sampling: { mode: 'original', fractions },
    color,
    start_time: new Date(points[0].timestampMs).toISOString(),
    end_time: new Date(points[points.length - 1].timestampMs).toISOString(),
  };
}

/** Compute time fractions [0..1] from the original timestamps of imported points. */
function computeTimeFractions(points: ParsedPoint[]): number[] {
  const startMs = points[0].timestampMs;
  const endMs = points[points.length - 1].timestampMs;
  const rangeMs = endMs - startMs;

  if (rangeMs === 0) {
    // All same timestamp — distribute evenly
    return points.map((_, i) => i / (points.length - 1));
  }

  return points.map((p) => (p.timestampMs - startMs) / rangeMs);
}

function buildWaypoints(points: ParsedPoint[]): WaypointAttributes[] {
  const waypoints: WaypointAttributes[] = [];

  for (let i = 0; i < points.length; i++) {
    const props = points[i].properties;

    let sogKn: number;
    if (typeof props.speed_over_ground_kn === 'number') {
      sogKn = props.speed_over_ground_kn;
    } else if (typeof props.sog === 'number') {
      sogKn = props.sog;
    } else if (i < points.length - 1) {
      // Calculate from distance and time between this point and the next
      sogKn = calculateSpeedKn(points[i], points[i + 1]);
    } else {
      // Last point — reuse previous speed
      sogKn = waypoints.length > 0 ? waypoints[waypoints.length - 1].sog_kn : 0;
    }

    const rotDegMin =
      typeof props.rate_of_turn_deg_min === 'number'
        ? props.rate_of_turn_deg_min
        : typeof props.rot === 'number'
          ? props.rot
          : 0;

    const wp: WaypointAttributes = {
      timestamp: new Date(points[i].timestampMs).toISOString(),
      sog_kn: Math.round(sogKn * 10) / 10,
      rot_deg_min: rotDegMin,
    };

    // Optional overrides from properties
    if (typeof props.name === 'string') wp.name = props.name;
    if (props.mmsi != null) wp.mmsi = String(props.mmsi);
    if (typeof props.imo === 'number') wp.imo = props.imo;
    if (typeof props.nav_status === 'string')
      wp.nav_status = props.nav_status as WaypointAttributes['nav_status'];
    if (typeof props.heading_deg === 'number')
      wp.heading_deg = props.heading_deg;
    else if (typeof props.cog === 'number') wp.heading_deg = props.cog;
    if (props.type === 'transponder_off' || props.type === 'outlier')
      wp.type = props.type;

    waypoints.push(wp);
  }

  return waypoints;
}

function calculateSpeedKn(a: ParsedPoint, b: ParsedPoint): number {
  const distMeters = haversineDistance(a.lon, a.lat, b.lon, b.lat);
  const timeDeltaMs = Math.abs(b.timestampMs - a.timestampMs);

  if (timeDeltaMs === 0) return 0;

  const distNm = distMeters / METERS_PER_NAUTICAL_MILE;
  const timeHours = timeDeltaMs / MS_PER_HOUR;
  const sog = distNm / timeHours;

  // Cap to filter GPS noise spikes (tiny time delta + position jitter)
  return Math.min(sog, MAX_REASONABLE_SOG_KN);
}

function extractSource(points: ParsedPoint[]): Source {
  // Use properties from the first point as the baseline
  const props = points[0].properties;

  const source: Source = {
    name: typeof props.name === 'string' ? props.name : 'Imported Vessel',
  };

  if (props.mmsi != null) source.mmsi = String(props.mmsi);
  if (typeof props.imo === 'number') source.imo = props.imo;
  if (typeof props.call_sign === 'string') source.callSign = props.call_sign;
  if (typeof props.flag === 'string') source.flag = props.flag;
  if (typeof props.ship_type === 'string') source.shipType = props.ship_type;

  // Map ais_type string to typed aisClass
  if (typeof props.ais_class === 'string') {
    source.aisClass = props.ais_class.toUpperCase() === 'B' ? 'B' : 'A';
  } else if (typeof props.ais_type === 'string') {
    source.aisClass = props.ais_type.toLowerCase().includes('b') ? 'B' : 'A';
  }
  if (typeof props.width_m === 'number') source.width_m = props.width_m;
  if (typeof props.length_m === 'number') source.length_m = props.length_m;
  if (typeof props.draught_m === 'number') source.draught_m = props.draught_m;
  if (typeof props.destination === 'string')
    source.destination = props.destination;
  if (typeof props.eta === 'string') source.eta = props.eta;

  return source;
}

function resolveDataType(props: Record<string, unknown>): DataType {
  if (typeof props.data_type === 'string') {
    const dt = props.data_type as DataType;
    if (['ais', 'radar', 'drone', 'das'].includes(dt)) return dt;
  }
  return 'ais';
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function stripExtension(filename: string): string {
  return filename.replace(/\.[^.]+$/, '');
}

/** Compute [minLon, minLat, maxLon, maxLat] from parsed points. */
function computeBounds(
  points: ParsedPoint[],
): [number, number, number, number] {
  let minLon = Infinity;
  let minLat = Infinity;
  let maxLon = -Infinity;
  let maxLat = -Infinity;

  for (const p of points) {
    if (p.lon < minLon) minLon = p.lon;
    if (p.lat < minLat) minLat = p.lat;
    if (p.lon > maxLon) maxLon = p.lon;
    if (p.lat > maxLat) maxLat = p.lat;
  }

  return [minLon, minLat, maxLon, maxLat];
}

const DEFAULT_COLORS = [
  '#ef4444',
  '#22c55e',
  '#f59e0b',
  '#ec4899',
  '#a855f7',
  '#f97316',
  '#fbbf24',
  '#e879f9',
  '#4ade80',
  '#fb923c',
];
